import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthApiError } from '@/api/client';
import { errorMessage } from '@/lib/errorMap';
import { useAuthStore } from '@/stores/useAuthStore';

export function LoginPage() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const login = useAuthStore((s) => s.login);
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await login(identifier, password);
      if (res.requiresMfa) {
        const redirect = params.get('redirect') ?? '/';
        const q = new URLSearchParams({
          cid: res.challengeId,
          redirect,
          method: res.method,
        });
        if (res.recipientHint) q.set('hint', res.recipientHint);
        navigate(`/mfa/challenge?${q.toString()}`, { replace: true });
        return;
      }
      toast.success('로그인 됐습니다');
      navigate(params.get('redirect') ?? '/', { replace: true });
    } catch (e) {
      if (e instanceof AuthApiError) toast.error(errorMessage(e.code));
      else toast.error('네트워크 오류');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>ontoh 로그인</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="identifier">아이디 (이메일 또는 person_id)</Label>
              <Input
                id="identifier"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="kim@example.com"
                autoComplete="username"
                required
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="password">비밀번호</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            <Button type="submit" disabled={submitting} className="mt-2">
              {submitting ? '로그인 중...' : '로그인'}
            </Button>
            <div className="flex justify-between text-sm">
              <Link to="/forgot-password" className="text-muted-foreground hover:underline">
                비밀번호 찾기
              </Link>
              <Link to="/signup" className="text-muted-foreground hover:underline">
                회원가입
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
